import { ComponentType, FunctionComponent, ReactNode, createContext, useContext, useMemo } from "react";
import type { SportsbookBetslipCapabilities, SportsbookBetslipContract } from "../../contract";
import { resolveSportsbookCapabilities } from "../../capabilities/sportsbook-resolver";
import { useSportsbookCapabilitiesSync } from "../../store/state/capabilities/useCapabilitiesSync";
import { useSportsbookPreferencesSync } from "../../store/state/preferences/usePreferencesSync";

export type SportsbookBetslipContext = Omit<SportsbookBetslipContract, "capabilities"> & {
  capabilities: SportsbookBetslipCapabilities;
};

const Context = createContext<SportsbookBetslipContext | null>(null);
Context.displayName = "SportsbookBetslipContext";

export function useSportsbookBetslipContext(): SportsbookBetslipContext {
  const context = useContext(Context);
  if (context === null) {
    throw new Error("SportsbookBetslipContext consumer must be used within a SportsbookBetslipContextProvider");
  }
  return context;
}

/**
 * The API entrance for the sportsbook contract: hosts may supply a partial (or absent)
 * `capabilities` group here, and every consumer downstream still sees a complete
 * `SportsbookBetslipContext` because the gaps are resolved before reaching `Context.Provider`.
 */
export const SportsbookBetslipContextProvider: FunctionComponent<{
  children: ReactNode;
  value: SportsbookBetslipContract;
}> = ({ children, value }) => {
  const capabilities = useMemo(() => resolveSportsbookCapabilities(value.capabilities), [value.capabilities]);
  const resolvedValue = useMemo<SportsbookBetslipContext>(() => ({ ...value, capabilities }), [value, capabilities]);

  useSportsbookCapabilitiesSync(capabilities);
  useSportsbookPreferencesSync(value.preferences);

  return <Context.Provider value={resolvedValue}>{children}</Context.Provider>;
};

export function withSportsbookBetslipContext<Selected extends object>(
  selector: (context: SportsbookBetslipContext) => Selected,
) {
  return function withSportsbookBetslipContextSelector<P extends Selected>(
    Component: ComponentType<P>,
  ): FunctionComponent<Omit<P, keyof Selected>> {
    return function WithSportsbookBetslipContext(props: Omit<P, keyof Selected>) {
      const selected = selector(useSportsbookBetslipContext());
      return <Component {...(props as P)} {...selected} />;
    };
  };
}
